/* ═══════════════════════════════════════════════════════════════
   NADÏELLE — Collection page behaviour  <ndl-collection>
   · filter / sort dropdowns (one open at a time, Esc / outside click closes)
   · ticking a filter or choosing a sort applies it instantly
   · results swap in place via the Section Rendering API (no reload),
     URL is kept in sync so filtered views are shareable / back-button safe
   · 4 / 2 column toggle, remembered per browser
   · sticky toolbar sits right under Horizon's header (whatever its sticky mode)
   If anything fails, it falls back to a normal page load.
   ═══════════════════════════════════════════════════════════════ */
(() => {
  if (customElements.get('ndl-collection')) return;

  const VIEW_KEY = 'ndl-collection-view';
  const SIZES = {
    '4': '(min-width: 990px) 25vw, 50vw',
    '2': '(min-width: 990px) 50vw, 100vw'
  };
  const store = {
    get() { try { return localStorage.getItem(VIEW_KEY); } catch (e) { return null; } },
    set(v) { try { localStorage.setItem(VIEW_KEY, v); } catch (e) {} }
  };

  class NdlCollection extends HTMLElement {
    connectedCallback() {
      this.classList.add('is-js');
      this.view = store.get() || this.dataset.defaultView || '4';
      this.applyView();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onDocClick = (e) => { if (!this.contains(e.target)) this.closePanels(); };
      this.onPop = () => { if (this.pushed) this.load(location.href, { push: false }); };

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('popstate', this.onPop);
      this.initSticky();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPop);
      if (this.stickyCleanup) this.stickyCleanup();
    }

    /* ── events ── */
    onClick(e) {
      const btn = e.target.closest('.ndl-filter__btn');
      if (btn) {
        const f = btn.closest('.ndl-filter');
        f.classList.contains('is-open') ? this.closePanels() : this.openPanel(f);
        return;
      }
      const viewBtn = e.target.closest('[data-view]');
      if (viewBtn) {
        this.view = viewBtn.dataset.view;
        store.set(this.view);
        this.applyView();
        return;
      }
      const link = e.target.closest('a[data-ndl-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
        e.preventDefault();
        this.load(link.href, { scroll: link.hasAttribute('data-scroll') });
        return;
      }
      if (!e.target.closest('.ndl-panel')) this.closePanels();
    }

    onChange(e) {
      const t = e.target;
      if (t.type === 'checkbox') this.submit({ keepOpen: true });
      else if (t.type === 'radio' && t.name === 'sort_by') this.submit({ keepOpen: false });
      // price inputs wait for Apply / Enter
    }

    onSubmit(e) {
      if (!e.target.matches('[data-filter-form]')) return;
      e.preventDefault();
      this.submit({ keepOpen: false });
    }

    onKey(e) {
      if (e.key !== 'Escape') return;
      const open = this.querySelector('.ndl-filter.is-open');
      if (!open) return;
      this.closePanels();
      open.querySelector('.ndl-filter__btn')?.focus();
    }

    /* ── panels ── */
    openPanel(f) {
      this.closePanels();
      f.classList.add('is-open');
      f.querySelector('.ndl-filter__btn')?.setAttribute('aria-expanded', 'true');
    }

    closePanels() {
      this.querySelectorAll('.ndl-filter.is-open').forEach((f) => {
        f.classList.remove('is-open');
        f.querySelector('.ndl-filter__btn')?.setAttribute('aria-expanded', 'false');
      });
    }

    /* ── grid layout ── */
    applyView() {
      const grid = this.querySelector('[data-grid]');
      if (grid) {
        grid.classList.toggle('is-2', this.view === '2');
        grid.querySelectorAll('img.ndl-card__img').forEach((img) => img.setAttribute('sizes', SIZES[this.view] || SIZES['4']));
      }
      this.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === this.view)));
    }

    /* ── filtering ── */
    submit({ keepOpen }) {
      const form = this.querySelector('[data-filter-form]');
      if (!form) return;
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
      const qs = params.toString();
      this.load(form.action.split('?')[0] + (qs ? '?' + qs : ''), { keepOpen });
    }

    async load(url, { push = true, keepOpen = false, scroll = false } = {}) {
      const openKey = keepOpen ? this.querySelector('.ndl-filter.is-open')?.dataset.filter : null;
      const focusId = keepOpen ? document.activeElement?.id : null;

      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      this.classList.add('is-loading');

      try {
        const fetchUrl = new URL(url, location.origin);
        fetchUrl.searchParams.set('section_id', this.dataset.section);
        const res = await fetch(fetchUrl, { signal: ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('ndl-collection');
        if (!fresh) throw new Error('section not found');

        ['[data-tabs]', '[data-toolbar]', '[data-results]'].forEach((sel) => {
          const oldEl = this.querySelector(sel);
          const newEl = fresh.querySelector(sel);
          if (oldEl && newEl) oldEl.replaceWith(document.importNode(newEl, true));
        });

        if (push) {
          history.pushState({ ndl: true }, '', url);
          this.pushed = true;
        }
        this.applyView();

        if (openKey) {
          const f = [...this.querySelectorAll('.ndl-filter')].find((el) => el.dataset.filter === openKey);
          if (f) this.openPanel(f);
        }
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        if (scroll) {
          const top = this.querySelector('[data-results]').getBoundingClientRect().top + window.scrollY
            - (parseFloat(getComputedStyle(this).getPropertyValue('--ndl-sticky-top')) || 0) - 60;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = url;          // safe fallback: normal navigation
      } finally {
        if (this.ctrl === ctrl) this.classList.remove('is-loading');
      }
    }

    /* ── sticky toolbar offset = visible bottom edge of the theme header ── */
    initSticky() {
      const header = document.querySelector('#header-component') || document.querySelector('.header-section');
      if (!header) return;
      let raf = 0;
      const update = () => {
        raf = 0;
        const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        this.style.setProperty('--ndl-sticky-top', bottom + 'px');
      };
      const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      let ro;
      if ('ResizeObserver' in window) { ro = new ResizeObserver(queue); ro.observe(header); }
      update();
      this.stickyCleanup = () => {
        window.removeEventListener('scroll', queue);
        window.removeEventListener('resize', queue);
        if (ro) ro.disconnect();
      };
    }
  }

  customElements.define('ndl-collection', NdlCollection);
})();
